import {Component, OnDestroy, ViewEncapsulation} from '@angular/core';
import {PartyEventProductResponse} from '@portal/api/responses/PartyEventProduct/party-event-product-response';
import {PartyEventProductsService} from '@portal/api/party-event-products.service';
import {SearchRequest} from '@portal/api/requests/search-request';
import {Guid} from 'guid-typescript';
import {ActivityBarService} from "./activity-bar.service";
import {AddActivityBarTransaction} from "@portal/api/contracts/activity-bar/add-activity-bar-transaction";
import {Success} from "@portal/api/responses/api-response";
import {ActivityBarTransaction} from "@portal/api/contracts/activity-bar/activity-bar-transaction";
import {SelectedBarService} from "../../activity-bar/selected-bar.service";
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {filter, takeUntil} from 'rxjs/operators';
import {forkJoin, Subject} from 'rxjs';
import {InventoryApiService} from '@portal/api/inventory/inventory-api.service';
import {PartyEventInventoryBalanceDto} from '@portal/api/inventory/inventory.models';
import { getInventoryCompositeLabel } from '../../../../inventory/inventory-display.utils';

@Component({
    selector: 'app-event-products-calculator',
    templateUrl: './event-products-calculator.component.html',
    styleUrls: ['./event-products-calculator.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class EventProductsCalculatorComponent implements OnDestroy {
    private static readonly LOW_STOCK_THRESHOLD = 5;
    eventId: Guid;
    barId: Guid;
    isCalculatorVisible: boolean;
    products: PartyEventProductResponse[];
    price: number;
    sortDirection = 1;
    isPartialDeletingCalculator: boolean;
    isChangePanelVisible: boolean;
    amountReceived: number | null = null;
    change: number = 0;
    showSuccessMessage = false;
    showErrorMessage = false;
    isLoading = false;
    isPanelLoading = false;
    errorMessage: string = '';
    totalRegistered: number = 0;
    private destroy$ = new Subject<void>();


    constructor(
        private eventProductsService: PartyEventProductsService,
        private eventProductsCalculatorService: ActivityBarService,
        private inventoryApi: InventoryApiService,
        private selectedBarService: SelectedBarService,
        private route: ActivatedRoute,
        private router: Router,
    ) {
    }

    ngOnInit(): void {
        this.isPanelLoading = true;
        
        this.eventProductsCalculatorService.connect().subscribe(res => {
            this.isCalculatorVisible = res.open;
            if (this.isCalculatorVisible) {
                this.hideLayout();

                this.eventId = res.eventId;
                this.barId = res.barId || null;
                
                if (this.eventId) {
                    if (this.barId) {
                        this.loadProductsForBar();
                    } else {
                        this.loadTotalRegistered();
                        this.isPanelLoading = false;
                    }
                }
            } else {
                this.resetLayout();
                this.products = [];
            }
        });

        // Listen to route changes to reload data when barId changes
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                // Update barId from route (fallback)
                const barIdParam = this.route.snapshot.paramMap.get('barId');
                const newBarId = barIdParam ? barIdParam as any : null;
                if (newBarId !== this.barId) {
                    this.barId = newBarId;
                    if (this.isCalculatorVisible && this.eventId && this.barId) {
                        this.loadProductsForBar();
                    } else if (this.isCalculatorVisible && this.eventId) {
                        this.loadTotalRegistered();
                    }
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadProductsForBar(): void {
        this.isPanelLoading = true;
        this.loadTotalRegisteredForCurrentBar();
        forkJoin({
            products: this.eventProductsService.listByEvent(
                {page: 0, pageSize: 600, sortDirection: this.sortDirection} as SearchRequest,
                this.eventId,
                this.barId),
            balances: this.inventoryApi.getPartyEventBalances(String(this.eventId), {
                page: 0,
                pageSize: 200
            })
        }).subscribe({
            next: ({products, balances}) => {
                const stockByVariant = new Map<string, number>(
                    (balances?.data?.items || []).map((item: PartyEventInventoryBalanceDto) => [
                        String(item.variantId),
                        Number(item.remainingAllocatedQty || 0)
                    ])
                );
                const stockByName = new Map<string, number>(
                    (balances?.data?.items || []).map((item: PartyEventInventoryBalanceDto) => [
                        this.normalizeInventoryKey(
                            getInventoryCompositeLabel(item.itemName, item.variantName, item.isTechnicalVariant)
                        ),
                        Number(item.remainingAllocatedQty || 0)
                    ])
                );

                this.products = (products?.data?.items || []).map((product: PartyEventProductResponse) => ({
                    ...product,
                    remainingInventoryQty: this.resolveProductStock(product, stockByVariant, stockByName)
                }));

                this.resetCalculatingScore();
                this.isPanelLoading = false;
            },
            error: () => {
                this.products = [];
                this.price = 0;
                this.isPanelLoading = false;
            }
        });
    }

    showChangePanel() {
        this.isCalculatorVisible = false;
        this.isChangePanelVisible = true;
    }

    onAmountReceivedChanged(value: number | null) {
        if (value != null) {
            this.amountReceived = value;
            this.calculateChange();
        } else {
            this.amountReceived = null;
            this.change = 0;
        }
    }

    calculateChange() {
        if (this.amountReceived != null) {
            this.change = this.amountReceived - this.price;
        } else {
            this.change = 0;
        }
    }

    goBack() {
        this.isChangePanelVisible = false;
        this.isCalculatorVisible = true;
        this.amountReceived = null;
    }

    registerTransactionByChange() {
        this.registerTransaction();
        this.goBack();
    }

    registerTransaction() {
        this.isLoading = true;
        this.showSuccessMessage = false;
        this.showErrorMessage = false;
        this.errorMessage = '';
        const selectedItems = this.products
            .filter(product => product.touchedTimes > 0)
            .map(product => ({
                id: product.id,
                count: product.touchedTimes,
                price: product.price
            }));

        const selectedBarId = this.selectedBarService.getSelectedBarId();
        if (!selectedBarId) {
            return;
        }
        const request = new AddActivityBarTransaction(this.eventId, selectedBarId, selectedItems);

        this.eventProductsCalculatorService.addTransaction(request).subscribe(
            (resp: Success<any>) => {
                this.showSuccessMessage = true;
                setTimeout(() => {
                    this.showSuccessMessage = false;
                }, 3000);
                this.products.forEach(p => p.touchedTimes = 0);
                this.calculateScore();
                this.isLoading = false;
                this.loadProductsForBar();
            },
            (error) => {
                this.isLoading = false;
                this.showErrorMessage = true;
                this.errorMessage = error?.error?.error?.message || 'Ocorreu um erro ao registar a transação. Por favor, tente novamente.';

                setTimeout(() => {
                    this.showErrorMessage = false;
                }, 3000);
            }
        );

    }

    loadTotalRegistered(): void {
        this.eventProductsCalculatorService.getTotalRegistered(this.eventId).subscribe(
            (total: number) => {
                this.totalRegistered = total;
            },
            () => {
                this.totalRegistered = 0;
            }
        );
    }

    private loadTotalRegisteredForCurrentBar(): void {
        if (!this.eventId) {
            this.totalRegistered = 0;
            return;
        }

        if (!this.barId) {
            this.loadTotalRegistered();
            return;
        }

        this.eventProductsCalculatorService.getAllByBar<ActivityBarTransaction>(this.eventId, this.barId).subscribe({
            next: (response) => {
                this.totalRegistered = (response?.data || []).reduce((sum, transaction) => {
                    return sum + ((transaction as any).totalPrice ?? transaction.total ?? 0);
                }, 0);
            },
            error: () => {
                this.totalRegistered = 0;
            }
        });
    }

    private hideLayout() {
        const elem = document.getElementById('layout-elem');
        if (elem) {
            elem.style.display = 'none';
        }
    }

    private resetLayout() {
        const elem = document.getElementById('layout-elem');
        if (elem) {
            elem.style.display = 'block';
        }
    }

    calculateScore() {
        this.price = this.products.reduce((accumulator, obj) => {
            return accumulator + (obj.touchedTimes * obj.price);
        }, 0);
    }

    priceClicked(id: Guid) {
        const pointClickedIndex = this.products.findIndex(x => x.id === id);
        if (!this.isPartialDeletingCalculator) {
            this.products[pointClickedIndex].touchedTimes += 1;
        } else {
            if (this.products[pointClickedIndex].touchedTimes > 0) {
                this.products[pointClickedIndex].touchedTimes -= 1;
            }
        }
        this.calculateScore();
    }

    resetCalculatingScore() {
        this.products.map(x => x.touchedTimes = 0);
        this.price = 0;
    }

    close() {
        this.eventProductsCalculatorService.modalSet({open: false})
    }

    partialDeleteCalculator() {
        this.isPartialDeletingCalculator = !this.isPartialDeletingCalculator;
    }

    getProjectedStock(product: PartyEventProductResponse): number | null {
        if (product.remainingInventoryQty === null || product.remainingInventoryQty === undefined) {
            return null;
        }

        return Number(product.remainingInventoryQty) - Number(product.touchedTimes || 0);
    }

    getCurrentStock(product: PartyEventProductResponse): number | null {
        if (product.remainingInventoryQty === null || product.remainingInventoryQty === undefined) {
            return null;
        }

        return Number(product.remainingInventoryQty);
    }

    hasInventoryTracking(product: PartyEventProductResponse): boolean {
        return !!product.inventoryVariantId;
    }

    showStockBadge(product: PartyEventProductResponse): boolean {
        return this.getCurrentStock(product) !== null;
    }

    getStockBadgeText(product: PartyEventProductResponse): string {
        const projectedStock = this.getProjectedStock(product);
        if (projectedStock === null) {
            return '';
        }

        if (projectedStock < 0) {
            return `Sem stock (${projectedStock})`;
        }

        if (projectedStock === 0) {
            return 'Sem stock';
        }

        return `Stock: ${projectedStock}`;
    }

    getProductDisplayName(product: PartyEventProductResponse): string {
        if (product.inventoryItemName) {
            return getInventoryCompositeLabel(
                product.inventoryItemName,
                product.inventoryVariantName,
                product.inventoryVariantIsTechnical
            ) || product.name;
        }

        return product.name;
    }

    isLowStock(product: PartyEventProductResponse): boolean {
        const projectedStock = this.getProjectedStock(product);
        return projectedStock !== null && projectedStock <= EventProductsCalculatorComponent.LOW_STOCK_THRESHOLD;
    }

    isNegativeStock(product: PartyEventProductResponse): boolean {
        const projectedStock = this.getProjectedStock(product);
        return projectedStock !== null && projectedStock < 0;
    }

    private resolveProductStock(
        product: PartyEventProductResponse,
        stockByVariant: Map<string, number>,
        stockByName: Map<string, number>
    ): number | null {
        if (product.inventoryVariantId) {
            return stockByVariant.get(String(product.inventoryVariantId)) ?? 0;
        }

        const inventoryLabel = this.getProductDisplayName(product);
        if (!inventoryLabel || !product.inventoryItemName) {
            return null;
        }

        return stockByName.get(this.normalizeInventoryKey(inventoryLabel)) ?? null;
    }

    private normalizeInventoryKey(value: string): string {
        return String(value || '')
            .trim()
            .toLocaleLowerCase()
            .replace(/\s+/g, ' ');
    }
}
